/**
 * Water puddle hazard system.
 *
 * Starting from a configurable level threshold, places short patches of
 * water on corridor paths. Walking on water drains health at double speed.
 *
 * Water patches are placed deterministically using the maze's seeded RNG
 * so they regenerate identically from the same seed (no need to persist
 * water positions in the save file).
 */

import {
  TILE_SIZE,
  WATER_MIN_LEVEL,
  WATER_PATCH_MIN_LENGTH,
  WATER_PATCH_MAX_LENGTH,
  WATER_MAX_PATCHES_BASE,
  WATER_PATCHES_PER_LEVEL,
  WATER_COVERAGE_CAP,
} from '../config/game-config.js';
import { TEX_WATER, CellType } from '../utils/constants.js';
import MazeGenerator from './MazeGenerator.js';

export default class WaterSystem {
  /**
   * @param {Phaser.Scene} scene - The GameScene instance.
   * @param {number[][]} grid - The maze grid (expanded).
   * @param {number} level - Current game level.
   * @param {{ col: number, row: number }} startPos - Player start position (excluded from water).
   * @param {{ col: number, row: number }} exitPos - Exit position (excluded from water).
   * @param {number} [seed] - Optional seed for deterministic placement.
   */
  constructor(scene, grid, level, startPos, exitPos, seed) {
    /** @type {Phaser.Scene} */
    this.scene = scene;

    /** @type {number[][]} */
    this.grid = grid;

    /** @type {number} */
    this.level = level;

    /** @type {{ col: number, row: number }} */
    this.startPos = startPos;

    /** @type {{ col: number, row: number }} */
    this.exitPos = exitPos;

    /** @type {Set<string>} Set of "col,row" strings for O(1) lookup. */
    this.waterCells = new Set();

    /** @type {Phaser.GameObjects.Sprite[]} Water tile sprites for cleanup. */
    this.sprites = [];

    /** @type {Phaser.Tweens.Tween[]} Shimmer tweens for cleanup. */
    this.tweens = [];

    // Seeded RNG (simple LCG)
    this._rngState = seed ?? (Date.now() ^ 0xdeadbeef);

    // Only place water from the minimum level onward
    if (level >= WATER_MIN_LEVEL) {
      this._placeWaterPatches();
      this._renderWaterSprites();
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Check whether a grid cell is a water tile.
   *
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isWater(col, row) {
    return this.waterCells.has(`${col},${row}`);
  }

  /**
   * Destroy all water sprites and tweens. Call during level cleanup.
   */
  destroy() {
    for (const tween of this.tweens) {
      if (tween && tween.isPlaying) {
        tween.stop();
      }
    }
    this.tweens = [];

    for (const sprite of this.sprites) {
      if (sprite && sprite.active) {
        sprite.destroy();
      }
    }
    this.sprites = [];

    this.waterCells.clear();
    this.scene = null;
  }

  // ---------------------------------------------------------------
  // Private — placement
  // ---------------------------------------------------------------

  /**
   * Simple LCG pseudo-random number generator.
   * @private
   * @returns {number} Value in [0, 1).
   */
  _nextRandom() {
    this._rngState = (this._rngState * 1664525 + 1013904223) & 0x7fffffff;
    return this._rngState / 0x80000000;
  }

  /**
   * Return a random integer in [min, max] (inclusive).
   * @private
   */
  _randInt(min, max) {
    return min + Math.floor(this._nextRandom() * (max - min + 1));
  }

  /**
   * Find corridors (straight runs of PATH cells) in the maze.
   * A corridor is a sequence of 2+ adjacent PATH cells in a straight
   * line (horizontal or vertical).
   *
   * @private
   * @returns {{ cells: { col: number, row: number }[], direction: 'h' | 'v' }[]}
   */
  _findCorridors() {
    const corridors = [];
    const gridH = this.grid.length;
    const gridW = this.grid[0].length;

    // Horizontal corridors
    for (let row = 0; row < gridH; row++) {
      let run = [];
      for (let col = 0; col < gridW; col++) {
        if (this.grid[row][col] === CellType.PATH) {
          run.push({ col, row });
        } else {
          if (run.length >= 2) {
            corridors.push({ cells: run, direction: 'h' });
          }
          run = [];
        }
      }
      if (run.length >= 2) {
        corridors.push({ cells: run, direction: 'h' });
      }
    }

    // Vertical corridors
    for (let col = 0; col < gridW; col++) {
      let run = [];
      for (let row = 0; row < gridH; row++) {
        if (this.grid[row][col] === CellType.PATH) {
          run.push({ col, row });
        } else {
          if (run.length >= 2) {
            corridors.push({ cells: run, direction: 'v' });
          }
          run = [];
        }
      }
      if (run.length >= 2) {
        corridors.push({ cells: run, direction: 'v' });
      }
    }

    return corridors;
  }

  /**
   * Place water patches on randomly selected corridor segments.
   * Avoids the player start cell and exit cell.
   * @private
   */
  _placeWaterPatches() {
    const corridors = this._findCorridors();
    if (corridors.length === 0) return;

    // How many patches to attempt
    const levelsAboveMin = this.level - WATER_MIN_LEVEL;
    const targetPatches = WATER_MAX_PATCHES_BASE + levelsAboveMin * WATER_PATCHES_PER_LEVEL;

    // Coverage cap: don't flood more than WATER_COVERAGE_CAP of all path cells
    const pathCells = MazeGenerator.getPathCells(this.grid);
    const maxWaterCells = Math.floor(pathCells.length * WATER_COVERAGE_CAP);

    // Excluded positions (start and exit, plus their immediate neighbours)
    const excluded = new Set();
    excluded.add(`${this.startPos.col},${this.startPos.row}`);
    excluded.add(`${this.exitPos.col},${this.exitPos.row}`);
    // Also exclude 1-tile radius around start/exit for comfort
    for (const pos of [this.startPos, this.exitPos]) {
      for (const [dx, dy] of [[0, -1], [0, 1], [-1, 0], [1, 0]]) {
        excluded.add(`${pos.col + dx},${pos.row + dy}`);
      }
    }

    // Shuffle corridors using Fisher-Yates with our seeded RNG
    const shuffled = [...corridors];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(this._nextRandom() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    let patchesPlaced = 0;

    for (const corridor of shuffled) {
      if (patchesPlaced >= targetPatches) break;
      if (this.waterCells.size >= maxWaterCells) break;

      // Only use corridors long enough for a patch
      if (corridor.cells.length < WATER_PATCH_MIN_LENGTH) continue;

      // Pick a random sub-segment of the corridor
      const patchLen = this._randInt(
        WATER_PATCH_MIN_LENGTH,
        Math.min(WATER_PATCH_MAX_LENGTH, corridor.cells.length),
      );
      const maxStart = corridor.cells.length - patchLen;
      const startIdx = this._randInt(0, maxStart);

      // Collect candidate cells for this patch
      const candidates = [];
      let valid = true;
      for (let i = startIdx; i < startIdx + patchLen; i++) {
        const cell = corridor.cells[i];
        const key = `${cell.col},${cell.row}`;
        if (excluded.has(key) || this.waterCells.has(key)) {
          valid = false;
          break;
        }
        candidates.push(cell);
      }

      if (!valid || candidates.length === 0) continue;

      // Add the patch
      for (const cell of candidates) {
        this.waterCells.add(`${cell.col},${cell.row}`);
      }
      patchesPlaced++;
    }
  }

  // ---------------------------------------------------------------
  // Private — rendering
  // ---------------------------------------------------------------

  /**
   * Create water tile sprites in the scene for each water cell.
   * Sprites are placed above the floor but below the fog layer.
   * Uses a single batched tween to animate shimmer for all sprites,
   * with per-sprite phase offsets for visual variety.
   * @private
   */
  _renderWaterSprites() {
    if (!this.scene) return;

    /** @type {{ sprite: Phaser.GameObjects.Sprite, phase: number }[]} */
    const spriteData = [];

    for (const key of this.waterCells) {
      const [colStr, rowStr] = key.split(',');
      const col = parseInt(colStr, 10);
      const row = parseInt(rowStr, 10);

      const x = col * TILE_SIZE + TILE_SIZE / 2;
      const y = row * TILE_SIZE + TILE_SIZE / 2;

      const sprite = this.scene.add.sprite(x, y, TEX_WATER);
      sprite.setOrigin(0.5);
      sprite.setDepth(2); // Above walls (0), below player/food/coins (5) and fog (100)
      sprite.setAlpha(0.8);

      // Random phase offset for visual variety
      const phase = this._nextRandom() * Math.PI * 2;

      this.sprites.push(sprite);
      spriteData.push({ sprite, phase });
    }

    // Single batched shimmer tween: a dummy value from 0 to 1 drives all sprites
    if (spriteData.length > 0) {
      const shimmerState = { t: 0 };

      const shimmerTween = this.scene.tweens.add({
        targets: shimmerState,
        t: Math.PI * 2,
        duration: 1600,
        repeat: -1,
        ease: 'Linear',
        onUpdate: () => {
          for (const { sprite, phase } of spriteData) {
            if (!sprite.active) continue;
            // Oscillate alpha between 0.6 and 0.9 using sine wave with per-sprite phase
            sprite.setAlpha(0.75 + 0.15 * Math.sin(shimmerState.t + phase));
          }
        },
      });

      this.tweens.push(shimmerTween);
    }
  }
}
